import type {
  CommunicationDeliveryMode,
  CommunicationPurpose,
  CommunicationSuppressionReason,
  CustomerCommunicationStatus,
  WhatsAppIdentitySlot,
  WhatsAppSessionHealth,
} from './crm';
import type { PayableSettlement } from './payables';
import { PERMISSIONS } from './permissions';
import type { AgeingBucket } from './receivables';
import type { PaymentMethod } from './sales';

/**
 * Reports & Analytics (Phase 6): the contracts shared by the API and the web.
 *
 * Every figure is computed on the server from the transactional tables. A panel the caller may not
 * see is absent from a response, never zero: unauthorised is not the same fact as nothing happened.
 */

// ---------------------------------------------------------------------------
// Areas, panels and the permission each one demands
// ---------------------------------------------------------------------------

/** The seven report areas. There is no Clinical area and no separate AI area. */
export const ReportArea = {
  OVERVIEW: 'OVERVIEW',
  SALES: 'SALES',
  INVENTORY: 'INVENTORY',
  FINANCE: 'FINANCE',
  CUSTOMERS: 'CUSTOMERS',
  OPTICAL: 'OPTICAL',
  CRM: 'CRM',
} as const;
export type ReportArea = (typeof ReportArea)[keyof typeof ReportArea];

export const REPORT_AREAS: readonly ReportArea[] = [
  ReportArea.OVERVIEW,
  ReportArea.SALES,
  ReportArea.INVENTORY,
  ReportArea.FINANCE,
  ReportArea.CUSTOMERS,
  ReportArea.OPTICAL,
  ReportArea.CRM,
];

/** The unit of authorisation: each panel is granted or refused on its own, never as a whole page. */
export const ReportPanel = {
  SALES: 'SALES',
  INVENTORY_QUANTITIES: 'INVENTORY_QUANTITIES',
  INVENTORY_VALUE: 'INVENTORY_VALUE',
  INVENTORY_SALES_ACTIVITY: 'INVENTORY_SALES_ACTIVITY',
  FINANCE_COLLECTIONS: 'FINANCE_COLLECTIONS',
  FINANCE_RECEIVABLES: 'FINANCE_RECEIVABLES',
  FINANCE_SUPPLIER_OBLIGATIONS: 'FINANCE_SUPPLIER_OBLIGATIONS',
  FINANCE_PAYABLES: 'FINANCE_PAYABLES',
  FINANCE_EXPENSES: 'FINANCE_EXPENSES',
  CUSTOMERS_REGISTRATIONS: 'CUSTOMERS_REGISTRATIONS',
  CUSTOMERS_VALUE: 'CUSTOMERS_VALUE',
  CUSTOMERS_OUTSTANDING: 'CUSTOMERS_OUTSTANDING',
  OPTICAL: 'OPTICAL',
  OPTICAL_MESSAGE_STATE: 'OPTICAL_MESSAGE_STATE',
  CRM_COMMUNICATIONS: 'CRM_COMMUNICATIONS',
  CRM_WHATSAPP_HEALTH: 'CRM_WHATSAPP_HEALTH',
  CRM_AUTOMATION_SETTINGS: 'CRM_AUTOMATION_SETTINGS',
} as const;
export type ReportPanel = (typeof ReportPanel)[keyof typeof ReportPanel];

export interface ReportPanelDefinition {
  area: Exclude<ReportArea, 'OVERVIEW'>;
  /** Every key is required. `reporting.view` is always the first. */
  requires: readonly string[];
}

const RV = PERMISSIONS.REPORTING_VIEW;

/**
 * The approved Phase 6.1 matrix, as data.
 *
 * Stricter than the operational screens where it matters: a receivable reveals what was paid, so it
 * needs `payments.view`; a stock value is cost information, so it needs `catalog.view`.
 */
export const REPORT_PANELS: Readonly<Record<ReportPanel, ReportPanelDefinition>> = {
  SALES: { area: 'SALES', requires: [RV, PERMISSIONS.SALES_VIEW] },
  INVENTORY_QUANTITIES: { area: 'INVENTORY', requires: [RV, PERMISSIONS.INVENTORY_VIEW] },
  INVENTORY_VALUE: {
    area: 'INVENTORY',
    requires: [RV, PERMISSIONS.INVENTORY_VIEW, PERMISSIONS.CATALOG_VIEW],
  },
  // Phase 6.4: stock interrogated against confirmed sales. Inventory authority alone never reads Sales.
  INVENTORY_SALES_ACTIVITY: {
    area: 'INVENTORY',
    requires: [RV, PERMISSIONS.INVENTORY_VIEW, PERMISSIONS.SALES_VIEW],
  },
  FINANCE_COLLECTIONS: {
    area: 'FINANCE',
    requires: [RV, PERMISSIONS.SALES_VIEW, PERMISSIONS.PAYMENTS_VIEW],
  },
  FINANCE_RECEIVABLES: {
    area: 'FINANCE',
    requires: [RV, PERMISSIONS.SALES_VIEW, PERMISSIONS.PAYMENTS_VIEW],
  },
  FINANCE_SUPPLIER_OBLIGATIONS: {
    area: 'FINANCE',
    requires: [RV, PERMISSIONS.PURCHASING_VIEW, PERMISSIONS.PAYABLES_VIEW],
  },
  FINANCE_PAYABLES: { area: 'FINANCE', requires: [RV, PERMISSIONS.PAYABLES_VIEW] },
  FINANCE_EXPENSES: { area: 'FINANCE', requires: [RV, PERMISSIONS.EXPENSES_VIEW] },
  CUSTOMERS_REGISTRATIONS: { area: 'CUSTOMERS', requires: [RV, PERMISSIONS.CUSTOMERS_VIEW] },
  CUSTOMERS_VALUE: {
    area: 'CUSTOMERS',
    requires: [RV, PERMISSIONS.CUSTOMERS_VIEW, PERMISSIONS.SALES_VIEW],
  },
  CUSTOMERS_OUTSTANDING: {
    area: 'CUSTOMERS',
    requires: [RV, PERMISSIONS.CUSTOMERS_VIEW, PERMISSIONS.SALES_VIEW, PERMISSIONS.PAYMENTS_VIEW],
  },
  OPTICAL: { area: 'OPTICAL', requires: [RV, PERMISSIONS.OPTICAL_VIEW] },
  OPTICAL_MESSAGE_STATE: {
    area: 'OPTICAL',
    requires: [
      RV,
      PERMISSIONS.OPTICAL_VIEW,
      PERMISSIONS.NOTIFICATIONS_VIEW,
      PERMISSIONS.CUSTOMERS_VIEW,
    ],
  },
  CRM_COMMUNICATIONS: {
    area: 'CRM',
    requires: [RV, PERMISSIONS.NOTIFICATIONS_VIEW, PERMISSIONS.CUSTOMERS_VIEW],
  },
  CRM_WHATSAPP_HEALTH: { area: 'CRM', requires: [RV, PERMISSIONS.NOTIFICATIONS_VIEW] },
  CRM_AUTOMATION_SETTINGS: {
    area: 'CRM',
    requires: [RV, PERMISSIONS.NOTIFICATIONS_VIEW, PERMISSIONS.AUTOMATIONS_VIEW],
  },
};

export const REPORT_PANEL_KEYS = Object.keys(REPORT_PANELS) as ReportPanel[];

/** Settings are read with the reports; changing them is tenant policy. */
export const REPORTING_SETTINGS_READ_PERMISSION = PERMISSIONS.REPORTING_VIEW;
export const REPORTING_SETTINGS_WRITE_PERMISSION = PERMISSIONS.TENANT_UPDATE;

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const ReportWeekStart = {
  SATURDAY: 'SATURDAY',
  SUNDAY: 'SUNDAY',
  MONDAY: 'MONDAY',
} as const;
export type ReportWeekStart = (typeof ReportWeekStart)[keyof typeof ReportWeekStart];

export const REPORT_WEEK_STARTS: readonly ReportWeekStart[] = ['SATURDAY', 'SUNDAY', 'MONDAY'];

export interface ReportingSettingsView {
  weekStartsOn: ReportWeekStart;
  readyForCollectionAttentionDays: number;
  labStageAttentionDays: number;
  orderedNotSentAttentionDays: number;
  /** Reserved. The Phase 6.8 Smart Report Builder is deterministic, sends nothing anywhere and is not gated by either switch. */
  smartReportsEnabled: boolean;
  smartReportAiSummaryEnabled: boolean;
  version: number;
}

export type UpdateReportingSettingsRequest = Partial<Omit<ReportingSettingsView, 'version'>> & {
  version: number;
};

export const REPORTING_ATTENTION_DAYS_MIN = 1;
export const REPORTING_ATTENTION_DAYS_MAX = 365;

export const DEFAULT_REPORTING_SETTINGS: Omit<ReportingSettingsView, 'version'> = {
  weekStartsOn: ReportWeekStart.SATURDAY,
  readyForCollectionAttentionDays: 7,
  labStageAttentionDays: 10,
  orderedNotSentAttentionDays: 3,
  smartReportsEnabled: false,
  smartReportAiSummaryEnabled: true,
};

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------

export const ReportPreset = {
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  THIS_WEEK: 'THIS_WEEK',
  LAST_7_DAYS: 'LAST_7_DAYS',
  LAST_30_DAYS: 'LAST_30_DAYS',
  THIS_MONTH: 'THIS_MONTH',
  LAST_MONTH: 'LAST_MONTH',
  THIS_QUARTER: 'THIS_QUARTER',
  THIS_YEAR: 'THIS_YEAR',
  CUSTOM: 'CUSTOM',
} as const;
export type ReportPreset = (typeof ReportPreset)[keyof typeof ReportPreset];

export const REPORT_PRESETS = Object.values(ReportPreset) as ReportPreset[];

/** A custom range may cover at most this many calendar days, both ends included. */
export const REPORT_MAX_RANGE_DAYS = 731;

export const ReportGranularity = { DAY: 'DAY', WEEK: 'WEEK', MONTH: 'MONTH' } as const;
export type ReportGranularity = (typeof ReportGranularity)[keyof typeof ReportGranularity];

export interface ReportDateRange {
  /** Inclusive calendar dates, `YYYY-MM-DD`. */
  from: string;
  to: string;
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** One currency's amount. Currencies are never converted, merged or dropped. */
export interface MoneyAmount {
  currencyCode: string;
  amountMinor: number;
}

/** One entry per currency present, base currency first; an empty result is one base-currency zero. */
export type MoneyByCurrency = MoneyAmount[];

export interface MoneyChange {
  currencyCode: string;
  absoluteMinor: number;
  /** Null when the previous value is zero: shown as N/A, never infinity. */
  pctBasisPoints: number | null;
}

export interface CountChange {
  absolute: number;
  pctBasisPoints: number | null;
}

// ---------------------------------------------------------------------------
// The semantic catalogue
// ---------------------------------------------------------------------------

export const ReportMetricType = { FLOW: 'FLOW', SNAPSHOT: 'SNAPSHOT' } as const;
export type ReportMetricType = (typeof ReportMetricType)[keyof typeof ReportMetricType];

export const ReportMetricUnit = {
  MONEY: 'MONEY',
  COUNT: 'COUNT',
  BASIS_POINTS: 'BASIS_POINTS',
  DURATION_SECONDS: 'DURATION_SECONDS',
} as const;
export type ReportMetricUnit = (typeof ReportMetricUnit)[keyof typeof ReportMetricUnit];

/** Only AVAILABLE metrics may enter an executable plan; PLANNED ones are documentation until their slice ships. */
export const ReportMetricStatus = { AVAILABLE: 'AVAILABLE', PLANNED: 'PLANNED' } as const;
export type ReportMetricStatus = (typeof ReportMetricStatus)[keyof typeof ReportMetricStatus];

export const ReportDateSemantics = {
  BRANCH_LOCAL_TIMESTAMP: 'BRANCH_LOCAL_TIMESTAMP',
  CALENDAR_DATE: 'CALENDAR_DATE',
  AS_OF_NOW: 'AS_OF_NOW',
} as const;
export type ReportDateSemantics = (typeof ReportDateSemantics)[keyof typeof ReportDateSemantics];

export const ReportDimension = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  BRANCH: 'BRANCH',
  OPERATOR: 'OPERATOR',
  PRODUCT: 'PRODUCT',
  VARIANT: 'VARIANT',
  CATEGORY: 'CATEGORY',
  BRAND: 'BRAND',
  PRODUCT_TYPE: 'PRODUCT_TYPE',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  AGEING_BUCKET: 'AGEING_BUCKET',
  DUE_STATE: 'DUE_STATE',
  SUPPLIER: 'SUPPLIER',
  EXPENSE_CATEGORY: 'EXPENSE_CATEGORY',
  CUSTOMER: 'CUSTOMER',
  LAB: 'LAB',
  JOB_STAGE: 'JOB_STAGE',
  COMM_STATUS_GROUP: 'COMM_STATUS_GROUP',
  COMM_PURPOSE: 'COMM_PURPOSE',
  DELIVERY_MODE: 'DELIVERY_MODE',
  SUPPRESSION_REASON: 'SUPPRESSION_REASON',
  MOVEMENT_TYPE: 'MOVEMENT_TYPE',
  ADJUSTMENT_REASON_TYPE: 'ADJUSTMENT_REASON_TYPE',
  SETTLEMENT_STATUS: 'SETTLEMENT_STATUS',
  // Phase 6.7: a communication's own status, template and mapped failure code.
  COMM_STATUS: 'COMM_STATUS',
  COMM_TEMPLATE: 'COMM_TEMPLATE',
  FAILURE_REASON: 'FAILURE_REASON',
} as const;
export type ReportDimension = (typeof ReportDimension)[keyof typeof ReportDimension];

export const ReportFilterCode = {
  BRANCH: 'BRANCH',
  OPERATOR: 'OPERATOR',
  PRODUCT: 'PRODUCT',
  VARIANT: 'VARIANT',
  CATEGORY: 'CATEGORY',
  BRAND: 'BRAND',
  PRODUCT_TYPE: 'PRODUCT_TYPE',
  CUSTOMER: 'CUSTOMER',
  SUPPLIER: 'SUPPLIER',
  EXPENSE_CATEGORY: 'EXPENSE_CATEGORY',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  DUE_STATE: 'DUE_STATE',
  AMOUNT_AT_LEAST: 'AMOUNT_AT_LEAST',
  STAGNATION: 'STAGNATION',
  LAB: 'LAB',
  JOB_STATUS: 'JOB_STATUS',
  AGE_AT_LEAST_DAYS: 'AGE_AT_LEAST_DAYS',
  COMM_STATUS_GROUP: 'COMM_STATUS_GROUP',
  COMM_PURPOSE: 'COMM_PURPOSE',
  MOVEMENT_TYPE: 'MOVEMENT_TYPE',
  STOCK_HEALTH: 'STOCK_HEALTH',
  CURRENCY: 'CURRENCY',
  AGEING_BUCKET: 'AGEING_BUCKET',
  SETTLEMENT_STATUS: 'SETTLEMENT_STATUS',
  // Phase 6.7: the CRM tab's narrowing.
  COMM_STATUS: 'COMM_STATUS',
  COMM_TEMPLATE: 'COMM_TEMPLATE',
  DELIVERY_MODE: 'DELIVERY_MODE',
  SUPPRESSION_REASON: 'SUPPRESSION_REASON',
  FAILURE_REASON: 'FAILURE_REASON',
  // Phase 6.8: which optical attention list is counted — ready, at the lab, or ordered and not sent.
  OPTICAL_ATTENTION: 'OPTICAL_ATTENTION',
} as const;
export type ReportFilterCode = (typeof ReportFilterCode)[keyof typeof ReportFilterCode];

export interface ReportMetricDefinition {
  code: string;
  /** The KPI id in docs/reporting.md. */
  kpiId: string;
  area: Exclude<ReportArea, 'OVERVIEW'>;
  panel: ReportPanel;
  /** i18n keys under `reports.metrics.<code>`. */
  nameKey: string;
  descriptionKey: string;
  type: ReportMetricType;
  unit: ReportMetricUnit;
  dateSemantics: ReportDateSemantics;
  /** The column or rule the period is applied to, as documentation. */
  dateSource: string;
  perCurrency: boolean;
  comparable: boolean;
  drilldown: boolean;
  dimensions: readonly ReportDimension[];
  filters: readonly ReportFilterCode[];
  status: ReportMetricStatus;
}

const TIME = [ReportDimension.DAY, ReportDimension.WEEK, ReportDimension.MONTH] as const;

type MetricSeed = Omit<
  ReportMetricDefinition,
  'nameKey' | 'descriptionKey' | 'comparable' | 'drilldown'
> & { comparable?: boolean; drilldown?: boolean };

function metric(seed: MetricSeed): ReportMetricDefinition {
  return {
    ...seed,
    nameKey: `reports.metrics.${seed.code}.name`,
    descriptionKey: `reports.metrics.${seed.code}.description`,
    comparable: seed.comparable ?? seed.type === ReportMetricType.FLOW,
    drilldown: seed.drilldown ?? true,
  };
}

const SALES_DIMENSIONS = [
  ...TIME,
  ReportDimension.BRANCH,
  ReportDimension.OPERATOR,
  ReportDimension.PRODUCT,
  ReportDimension.VARIANT,
  ReportDimension.CATEGORY,
  ReportDimension.BRAND,
  ReportDimension.PRODUCT_TYPE,
] as const;
const SALES_FILTERS = [
  ReportFilterCode.BRANCH,
  ReportFilterCode.OPERATOR,
  ReportFilterCode.PRODUCT,
  ReportFilterCode.VARIANT,
  ReportFilterCode.CATEGORY,
  ReportFilterCode.BRAND,
  ReportFilterCode.PRODUCT_TYPE,
  ReportFilterCode.CUSTOMER,
] as const;
const STOCK_DIMENSIONS = [
  ReportDimension.BRANCH,
  ReportDimension.PRODUCT_TYPE,
  ReportDimension.CATEGORY,
  ReportDimension.BRAND,
] as const;
const STOCK_FILTERS = [
  ReportFilterCode.BRANCH,
  ReportFilterCode.PRODUCT_TYPE,
  ReportFilterCode.CATEGORY,
  ReportFilterCode.BRAND,
  ReportFilterCode.PRODUCT,
  ReportFilterCode.VARIANT,
] as const;
const STOCK_LINE_FILTERS = [...STOCK_FILTERS, ReportFilterCode.STOCK_HEALTH] as const;
/** Inventory flows: dated by their own event, attributed to a branch-local day. */
const INVENTORY_FLOW_DIMENSIONS = [...TIME, ...STOCK_DIMENSIONS] as const;

/** Money in: narrowed by who paid, how, and in which currency. */
const FINANCE_COLLECTION_FILTERS = [
  ReportFilterCode.BRANCH,
  ReportFilterCode.PAYMENT_METHOD,
  ReportFilterCode.CUSTOMER,
  ReportFilterCode.CURRENCY,
] as const;
/** Scheduled instalments: the ageing bucket narrows their records. */
const FINANCE_SCHEDULED_FILTERS = [
  ReportFilterCode.BRANCH,
  ReportFilterCode.CUSTOMER,
  ReportFilterCode.CURRENCY,
  ReportFilterCode.AGEING_BUCKET,
] as const;
const FINANCE_PAYABLE_FILTERS = [
  ReportFilterCode.BRANCH,
  ReportFilterCode.SUPPLIER,
  ReportFilterCode.CURRENCY,
  ReportFilterCode.AGEING_BUCKET,
  ReportFilterCode.SETTLEMENT_STATUS,
] as const;

/** Communications: every column of the row itself may narrow them. */
const CRM_COMMUNICATION_FILTERS = [
  ReportFilterCode.BRANCH,
  ReportFilterCode.COMM_PURPOSE,
  ReportFilterCode.COMM_STATUS,
  ReportFilterCode.DELIVERY_MODE,
  ReportFilterCode.COMM_TEMPLATE,
  ReportFilterCode.SUPPRESSION_REASON,
  ReportFilterCode.FAILURE_REASON,
] as const;

const A = ReportMetricStatus.AVAILABLE;
const P = ReportMetricStatus.PLANNED;
const LOCAL = ReportDateSemantics.BRANCH_LOCAL_TIMESTAMP;
const DATE = ReportDateSemantics.CALENDAR_DATE;
const NOW = ReportDateSemantics.AS_OF_NOW;
const { FLOW, SNAPSHOT } = ReportMetricType;
const { MONEY, COUNT } = ReportMetricUnit;

/**
 * The canonical semantic catalogue: what docs/reporting.md defines, what the tabs render and what a
 * future Smart Report may name. A PLANNED metric can be read about but never executed.
 */
export const REPORT_METRICS: readonly ReportMetricDefinition[] = [
  // Sales — after line discounts, before tax; reversals dated when they happen.
  metric({
    code: 'CONFIRMED_SALES',
    kpiId: 'S1',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'sales_orders.confirmed_at',
    perCurrency: true,
    dimensions: SALES_DIMENSIONS,
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'RETURNS',
    kpiId: 'S2',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'sales_credit_notes.issued_at / voided_at (kind RETURN)',
    perCurrency: true,
    dimensions: SALES_DIMENSIONS,
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'CANCELLATIONS',
    kpiId: 'S3',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'sales_orders.cancelled_at (confirmed_at not null)',
    perCurrency: true,
    dimensions: SALES_DIMENSIONS,
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'NET_SALES',
    kpiId: 'S4',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'S1 − S2 − S3',
    perCurrency: true,
    dimensions: SALES_DIMENSIONS,
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'CONFIRMED_ORDERS',
    kpiId: 'S5',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'sales_orders.confirmed_at',
    perCurrency: false,
    dimensions: SALES_DIMENSIONS,
    filters: SALES_FILTERS,
    status: A,
  }),
  // Two different things wear the word "cancelled", and only the first one is money. A confirmed
  // sale that is later cancelled reverses revenue that was already counted; an order cancelled
  // before it was ever confirmed never entered the books at all and must never be subtracted from
  // anything. The codes say which is which, because `CANCELLED_ORDERS` would have read as both.
  metric({
    code: 'CANCELLED_CONFIRMED_ORDERS',
    kpiId: 'S6',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'sales_orders.cancelled_at (confirmed_at not null)',
    perCurrency: false,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.OPERATOR],
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'ORDERS_CANCELLED_BEFORE_CONFIRMATION',
    kpiId: 'S11',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'sales_orders.cancelled_at (confirmed_at is null)',
    perCurrency: false,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.OPERATOR],
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'AVERAGE_ORDER_VALUE',
    kpiId: 'S7',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'S1 ÷ S5',
    perCurrency: true,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.OPERATOR],
    filters: SALES_FILTERS,
    status: A,
  }),
  // The operational half of a return: goods coming back, counted when the return was taken. Its
  // money lives in S2, which the credit note dates — the two can legitimately fall on different
  // days, so they are separate metrics rather than two readings of one.
  metric({
    code: 'RETURNS_RECORDED',
    kpiId: 'S9',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'sales_returns.created_at',
    perCurrency: false,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.PRODUCT, ReportDimension.VARIANT],
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'RETURNED_QUANTITY',
    kpiId: 'S10',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'sales_return_lines.quantity, dated by sales_returns.created_at',
    perCurrency: false,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.PRODUCT, ReportDimension.VARIANT],
    filters: SALES_FILTERS,
    status: A,
  }),
  metric({
    code: 'NET_TAX',
    kpiId: 'S8',
    area: 'SALES',
    panel: 'SALES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'tax on S1 − S2 − S3',
    perCurrency: true,
    dimensions: SALES_DIMENSIONS,
    filters: SALES_FILTERS,
    status: P,
  }),

  // Finance — money in.
  metric({
    code: 'COLLECTIONS',
    kpiId: 'C1',
    area: 'FINANCE',
    panel: 'FINANCE_COLLECTIONS',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'payment_transactions.occurred_at (PAYMENT − REFUND)',
    perCurrency: true,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.PAYMENT_METHOD],
    filters: FINANCE_COLLECTION_FILTERS,
    status: A,
  }),
  metric({
    code: 'REFUNDS_PAID',
    kpiId: 'C2',
    area: 'FINANCE',
    panel: 'FINANCE_COLLECTIONS',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource: 'payment_transactions.occurred_at (REFUND)',
    perCurrency: true,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.PAYMENT_METHOD],
    filters: FINANCE_COLLECTION_FILTERS,
    status: A,
  }),
  metric({
    code: 'RECEIVABLES_OUTSTANDING',
    kpiId: 'C4',
    area: 'FINANCE',
    panel: 'FINANCE_RECEIVABLES',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'orderReceivable over orders not CANCELLED, by the order branch',
    perCurrency: true,
    dimensions: [ReportDimension.BRANCH, ReportDimension.CUSTOMER],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.CUSTOMER, ReportFilterCode.CURRENCY],
    status: A,
  }),
  metric({
    code: 'CUSTOMERS_WITH_BALANCE',
    kpiId: 'C5',
    area: 'FINANCE',
    panel: 'FINANCE_RECEIVABLES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'distinct customer with C4 > 0',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: P,
  }),
  metric({
    code: 'OVERDUE_INSTALMENTS',
    kpiId: 'C6',
    area: 'FINANCE',
    panel: 'FINANCE_RECEIVABLES',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'payment_plan_installments.due_date < tenant today',
    perCurrency: true,
    dimensions: [ReportDimension.BRANCH, ReportDimension.AGEING_BUCKET, ReportDimension.CUSTOMER],
    filters: FINANCE_SCHEDULED_FILTERS,
    status: A,
  }),
  // Phase 6.5: the instalment population ageing covers. Not the same as C4 and never reconciled to it.
  metric({
    code: 'SCHEDULED_RECEIVABLES',
    kpiId: 'C8',
    area: 'FINANCE',
    panel: 'FINANCE_RECEIVABLES',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'active payment_plan_installments − receivable_allocations, by the plan branch',
    perCurrency: true,
    dimensions: [ReportDimension.BRANCH, ReportDimension.AGEING_BUCKET, ReportDimension.CUSTOMER],
    filters: FINANCE_SCHEDULED_FILTERS,
    status: A,
  }),
  metric({
    code: 'REFUNDS_DUE',
    kpiId: 'C7',
    area: 'FINANCE',
    panel: 'FINANCE_RECEIVABLES',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'refundableExcess',
    perCurrency: true,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: P,
  }),

  // Finance — money out.
  metric({
    code: 'SUPPLIER_OBLIGATIONS',
    kpiId: 'P1',
    area: 'FINANCE',
    panel: 'FINANCE_SUPPLIER_OBLIGATIONS',
    type: FLOW,
    unit: MONEY,
    dateSemantics: DATE,
    dateSource: 'purchase_invoices.invoice_date (POSTED)',
    perCurrency: true,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.SUPPLIER],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.SUPPLIER, ReportFilterCode.CURRENCY],
    status: A,
  }),
  metric({
    code: 'GOODS_RECEIVED_VALUE',
    kpiId: 'P2',
    area: 'FINANCE',
    panel: 'FINANCE_SUPPLIER_OBLIGATIONS',
    type: FLOW,
    unit: MONEY,
    dateSemantics: DATE,
    dateSource: 'purchase_receipts.received_on',
    perCurrency: true,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.SUPPLIER],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.SUPPLIER],
    status: P,
  }),
  metric({
    code: 'SUPPLIER_PAYMENTS',
    kpiId: 'P3',
    area: 'FINANCE',
    panel: 'FINANCE_PAYABLES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: DATE,
    dateSource: 'supplier_payments.payment_date (POSTED), each payment once',
    perCurrency: true,
    dimensions: [
      ...TIME,
      ReportDimension.BRANCH,
      ReportDimension.SUPPLIER,
      ReportDimension.PAYMENT_METHOD,
    ],
    filters: [
      ReportFilterCode.BRANCH,
      ReportFilterCode.SUPPLIER,
      ReportFilterCode.PAYMENT_METHOD,
      ReportFilterCode.CURRENCY,
    ],
    status: A,
  }),
  metric({
    code: 'PAYABLES_OUTSTANDING',
    kpiId: 'P4',
    area: 'FINANCE',
    panel: 'FINANCE_PAYABLES',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'POSTED invoices − allocations of POSTED payments',
    perCurrency: true,
    dimensions: [
      ReportDimension.BRANCH,
      ReportDimension.SUPPLIER,
      ReportDimension.AGEING_BUCKET,
      ReportDimension.SETTLEMENT_STATUS,
    ],
    filters: FINANCE_PAYABLE_FILTERS,
    status: A,
  }),
  metric({
    code: 'PAYABLES_OVERDUE',
    kpiId: 'P5',
    area: 'FINANCE',
    panel: 'FINANCE_PAYABLES',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'P4 where due_date < tenant today',
    perCurrency: true,
    dimensions: [ReportDimension.BRANCH, ReportDimension.SUPPLIER, ReportDimension.AGEING_BUCKET],
    filters: FINANCE_PAYABLE_FILTERS,
    status: A,
  }),
  metric({
    code: 'EXPENSES',
    kpiId: 'P6',
    area: 'FINANCE',
    panel: 'FINANCE_EXPENSES',
    type: FLOW,
    unit: MONEY,
    dateSemantics: DATE,
    dateSource: 'expenses.expense_date (POSTED)',
    perCurrency: true,
    dimensions: [
      ...TIME,
      ReportDimension.BRANCH,
      ReportDimension.EXPENSE_CATEGORY,
      ReportDimension.PAYMENT_METHOD,
    ],
    filters: [
      ReportFilterCode.BRANCH,
      ReportFilterCode.EXPENSE_CATEGORY,
      ReportFilterCode.PAYMENT_METHOD,
      ReportFilterCode.SUPPLIER,
      ReportFilterCode.CURRENCY,
    ],
    status: A,
  }),

  // Inventory — snapshots.
  metric({
    code: 'UNITS_ON_HAND',
    kpiId: 'I1',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'stock_items.quantity_on_hand > 0, per product type — never summed across types',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'ESTIMATED_INVENTORY_VALUE',
    kpiId: 'I2',
    area: 'INVENTORY',
    panel: 'INVENTORY_VALUE',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'Σ qty × positive catalogue cost, costed lines only',
    perCurrency: true,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'COST_COVERAGE',
    kpiId: 'I3',
    area: 'INVENTORY',
    panel: 'INVENTORY_VALUE',
    type: SNAPSHOT,
    unit: ReportMetricUnit.BASIS_POINTS,
    dateSemantics: NOW,
    dateSource: 'costed on-hand lines ÷ on-hand lines',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'LOW_STOCK_LINES',
    kpiId: 'I4',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'inventory mapper isLowStock',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'OUT_OF_STOCK_LINES',
    kpiId: 'I5',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'inventory mapper isOutOfStock',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'SLOW_MOVING_LINES',
    kpiId: 'I6',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'outbound baseline, slow ≤ days < dead',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'DEAD_STOCK_LINES',
    kpiId: 'I7',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'outbound baseline, days ≥ dead',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'UNKNOWN_MOVEMENT_LINES',
    kpiId: 'I8',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'no outbound and no recorded arrival',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'UNITS_IN_TRANSIT',
    kpiId: 'I9',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource:
      'quantity − received_quantity on DISPATCHED and PARTIALLY_RECEIVED transfers, per product type',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'ON_HAND_STOCK_LINES',
    kpiId: 'I10',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'stock_items with quantity_on_hand > 0',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_LINE_FILTERS,
    status: A,
  }),
  metric({
    code: 'RECENT_MOVEMENT_LINES',
    kpiId: 'I11',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'ageing baseline younger than the slow threshold',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_LINE_FILTERS,
    status: A,
  }),
  metric({
    code: 'BELOW_AGEING_MINIMUM_LINES',
    kpiId: 'I12',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: '0 < quantity_on_hand < max(1, minimum_on_hand_for_slow_moving)',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_LINE_FILTERS,
    status: A,
  }),
  metric({
    code: 'STOCK_MOVEMENTS',
    kpiId: 'I13',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'stock_transactions.created_at; quantity per product type only',
    perCurrency: false,
    comparable: false,
    dimensions: [...INVENTORY_FLOW_DIMENSIONS, ReportDimension.MOVEMENT_TYPE],
    filters: [...STOCK_FILTERS, ReportFilterCode.MOVEMENT_TYPE],
    status: A,
  }),
  metric({
    code: 'TRANSFERS_CREATED',
    kpiId: 'I14',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'stock_transfers.created_at (source branch calendar)',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'TRANSFERS_DISPATCHED',
    kpiId: 'I15',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'stock_transfers.dispatched_at (source branch calendar)',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'TRANSFERS_COMPLETED',
    kpiId: 'I16',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource:
      'stock_transfers.received_at, RECEIVED or RECEIVED_WITH_VARIANCE (destination calendar)',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'TRANSFER_VARIANCE_UNITS',
    kpiId: 'I17',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource:
      'received_quantity − quantity (the Transfers module sign) on RECEIVED_WITH_VARIANCE lines, by received_at',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ...STOCK_DIMENSIONS],
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'STOCKTAKES_POSTED',
    kpiId: 'I18',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'stocktake_sessions.posted_at, status POSTED',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'STOCKTAKE_VARIANCE_UNITS',
    kpiId: 'I19',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource:
      'counted_quantity − expected_quantity_at_count on POSTED sessions, per product type',
    perCurrency: false,
    comparable: false,
    dimensions: INVENTORY_FLOW_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'INVENTORY_ADJUSTMENTS',
    kpiId: 'I20',
    area: 'INVENTORY',
    panel: 'INVENTORY_QUANTITIES',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'stock_transactions type ADJUSTMENT; in and out kept apart, per product type',
    perCurrency: false,
    comparable: false,
    dimensions: [...INVENTORY_FLOW_DIMENSIONS, ReportDimension.ADJUSTMENT_REASON_TYPE],
    filters: STOCK_FILTERS,
    status: A,
  }),
  metric({
    code: 'IN_STOCK_NO_CONFIRMED_SALES',
    kpiId: 'I21',
    area: 'INVENTORY',
    // Interrogates the Sales domain, so its panel needs sales.view on top of the Inventory keys.
    panel: 'INVENTORY_SALES_ACTIVITY',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource:
      'current on-hand line with no order line of that variant at that branch confirmed in the period',
    perCurrency: false,
    dimensions: STOCK_DIMENSIONS,
    filters: STOCK_FILTERS,
    status: A,
  }),

  // Customers.
  metric({
    code: 'NEW_CUSTOMERS_REGISTERED',
    kpiId: 'K1',
    area: 'CUSTOMERS',
    panel: 'CUSTOMERS_REGISTRATIONS',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource:
      'customers.created_at by created_branch_id; no registration branch counted apart, on the tenant calendar',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'BUYING_CUSTOMERS',
    kpiId: 'K2',
    area: 'CUSTOMERS',
    panel: 'CUSTOMERS_VALUE',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'distinct sales_orders.customer_id on S1 confirmations, by the order branch',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'RETURNING_CUSTOMERS',
    kpiId: 'K3',
    area: 'CUSTOMERS',
    panel: 'CUSTOMERS_VALUE',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'K2 with an order confirmed in scope before the period began',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'FIRST_TIME_BUYERS',
    kpiId: 'K4',
    area: 'CUSTOMERS',
    panel: 'CUSTOMERS_VALUE',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'K2 − K3',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  // Phase 6.6: canonical S4 attributed to the order's customer; walk-in sales are reported apart and complete the total.
  metric({
    code: 'CUSTOMER_NET_SALES',
    kpiId: 'K5',
    area: 'CUSTOMERS',
    panel: 'CUSTOMERS_VALUE',
    type: FLOW,
    unit: MONEY,
    dateSemantics: LOCAL,
    dateSource:
      'S4 events of orders with a customer, by sales_orders.customer_id and the order branch',
    perCurrency: true,
    comparable: false,
    dimensions: [ReportDimension.BRANCH, ReportDimension.CUSTOMER],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.CUSTOMER],
    status: A,
  }),
  // Phase 6.6: canonical C4 attributed to the order's customer, never to where the customer registered.
  metric({
    code: 'CUSTOMER_RECEIVABLES_OUTSTANDING',
    kpiId: 'K6',
    area: 'CUSTOMERS',
    panel: 'CUSTOMERS_OUTSTANDING',
    type: SNAPSHOT,
    unit: MONEY,
    dateSemantics: NOW,
    dateSource: 'C4 over orders with a customer, by sales_orders.customer_id and the order branch',
    perCurrency: true,
    dimensions: [ReportDimension.BRANCH, ReportDimension.CUSTOMER],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.CUSTOMER],
    status: A,
  }),
  metric({
    code: 'CUSTOMERS_OWING',
    kpiId: 'K7',
    area: 'CUSTOMERS',
    panel: 'CUSTOMERS_OUTSTANDING',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'distinct customer with K6 > 0',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),

  // Optical.
  metric({
    code: 'ACTIVE_JOBS',
    kpiId: 'O1',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'optical_jobs.status ∈ ORDERED…RECEIVED_FROM_LAB',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH, ReportDimension.LAB, ReportDimension.JOB_STAGE],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB, ReportFilterCode.JOB_STATUS],
    status: A,
  }),
  metric({
    code: 'READY_FOR_COLLECTION',
    kpiId: 'O2',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'optical_jobs.status = READY_FOR_COLLECTION',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH, ReportDimension.LAB],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: A,
  }),
  metric({
    code: 'JOBS_CREATED',
    kpiId: 'O3',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'optical_job_status_changes CREATED row, order branch',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.LAB],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: A,
  }),
  metric({
    code: 'JOBS_DELIVERED',
    kpiId: 'O4',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'optical_job_status_changes to DELIVERED, never a LEGACY_BACKFILL row',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.LAB],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: A,
  }),
  metric({
    code: 'JOBS_CANCELLED',
    kpiId: 'O5',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'optical_job_status_changes to CANCELLED, never a LEGACY_BACKFILL row',
    perCurrency: false,
    comparable: false,
    dimensions: [...TIME, ReportDimension.BRANCH, ReportDimension.LAB],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: A,
  }),
  metric({
    code: 'JOBS_PAST_ATTENTION',
    kpiId: 'O6',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'entry into the current status vs ReportingSettings thresholds',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH, ReportDimension.JOB_STAGE],
    filters: [
      ReportFilterCode.BRANCH,
      ReportFilterCode.AGE_AT_LEAST_DAYS,
      ReportFilterCode.OPTICAL_ATTENTION,
    ],
    status: A,
  }),
  metric({
    code: 'TURNAROUND',
    kpiId: 'O7',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: FLOW,
    unit: ReportMetricUnit.DURATION_SECONDS,
    dateSemantics: LOCAL,
    dateSource:
      'first history row CREATED→ORDERED to the one DELIVERED row after it, gaps between them allowed; start not recorded and invalid history excluded',
    perCurrency: false,
    comparable: false,
    dimensions: [
      ReportDimension.WEEK,
      ReportDimension.MONTH,
      ReportDimension.BRANCH,
      ReportDimension.LAB,
    ],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: A,
  }),
  metric({
    code: 'STAGE_DURATION',
    kpiId: 'O8',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: FLOW,
    unit: ReportMetricUnit.DURATION_SECONDS,
    dateSemantics: LOCAL,
    dateSource: 'consecutive history rows',
    perCurrency: false,
    dimensions: [ReportDimension.JOB_STAGE, ReportDimension.BRANCH, ReportDimension.LAB],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: P,
  }),
  metric({
    code: 'SUPPLY_MODE_MIX',
    kpiId: 'O9',
    area: 'OPTICAL',
    panel: 'OPTICAL',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'optical_job_lenses.supply_mode of jobs whose CREATED row is in the period',
    perCurrency: false,
    comparable: false,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: A,
  }),
  // Phase 6.6: the latest ready-for-collection message of each job waiting for collection; never a delivery or read claim.
  metric({
    code: 'READY_MESSAGE_STATE',
    kpiId: 'O10',
    area: 'OPTICAL',
    panel: 'OPTICAL_MESSAGE_STATE',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource:
      'latest READY_FOR_COLLECTION customer_communications row of each READY_FOR_COLLECTION job',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH, ReportDimension.COMM_STATUS_GROUP],
    filters: [ReportFilterCode.BRANCH, ReportFilterCode.LAB],
    status: A,
  }),

  // CRM — truthful only: nothing here claims delivery or reading. PROVIDER_ACCEPTED is "sent to WhatsApp", never delivered.
  metric({
    code: 'COMMUNICATIONS',
    kpiId: 'M1',
    area: 'CRM',
    panel: 'CRM_COMMUNICATIONS',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'customer_communications.prepared_at',
    perCurrency: false,
    dimensions: [
      ...TIME,
      ReportDimension.BRANCH,
      ReportDimension.COMM_PURPOSE,
      ReportDimension.DELIVERY_MODE,
      ReportDimension.COMM_TEMPLATE,
    ],
    filters: CRM_COMMUNICATION_FILTERS,
    status: A,
  }),
  // Phase 6.7: every communication once, under its current status, dated by that status's own timestamp.
  metric({
    code: 'COMMUNICATION_OUTCOMES',
    kpiId: 'M2',
    area: 'CRM',
    panel: 'CRM_COMMUNICATIONS',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource:
      'the current status timestamp: prepared_at, queued_at, sending_at, provider_accepted_at, handed_off_at, confirmed_sent_at, failed_at, cancelled_at or suppressed_at',
    perCurrency: false,
    comparable: false,
    dimensions: [
      ReportDimension.BRANCH,
      ReportDimension.COMM_STATUS,
      ReportDimension.DELIVERY_MODE,
    ],
    filters: CRM_COMMUNICATION_FILTERS,
    status: A,
  }),
  metric({
    code: 'FAILED_COMMUNICATIONS',
    kpiId: 'M1-F',
    area: 'CRM',
    panel: 'CRM_COMMUNICATIONS',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'customer_communications.failed_at, status FAILED (OUTCOME_AMBIGUOUS split out)',
    perCurrency: false,
    comparable: false,
    dimensions: [ReportDimension.BRANCH, ReportDimension.FAILURE_REASON],
    filters: CRM_COMMUNICATION_FILTERS,
    status: A,
  }),
  // A suppression is a decision with a reason, never a failure, and is counted apart from one.
  metric({
    code: 'SUPPRESSED_COMMUNICATIONS',
    kpiId: 'M3',
    area: 'CRM',
    panel: 'CRM_COMMUNICATIONS',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'customer_communications.suppressed_at, status SUPPRESSED',
    perCurrency: false,
    comparable: false,
    dimensions: [ReportDimension.BRANCH, ReportDimension.SUPPRESSION_REASON],
    filters: CRM_COMMUNICATION_FILTERS,
    status: A,
  }),
  metric({
    code: 'OPEN_REMINDERS',
    kpiId: 'M4',
    area: 'CRM',
    panel: 'CRM_COMMUNICATIONS',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'customer_reminders OPEN',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: P,
  }),
  metric({
    code: 'REMINDERS_COMPLETED',
    kpiId: 'M5',
    area: 'CRM',
    panel: 'CRM_COMMUNICATIONS',
    type: FLOW,
    unit: COUNT,
    dateSemantics: LOCAL,
    dateSource: 'customer_reminders.completed_at',
    perCurrency: false,
    dimensions: [...TIME, ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: P,
  }),
  metric({
    code: 'WHATSAPP_HEALTH',
    kpiId: 'M6',
    area: 'CRM',
    panel: 'CRM_WHATSAPP_HEALTH',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'whatsapp_sessions of the active identity, as of now; no history is kept',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  metric({
    code: 'WHATSAPP_AUTOMATION_UNAVAILABLE',
    kpiId: 'M6-A',
    area: 'CRM',
    panel: 'CRM_WHATSAPP_HEALTH',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'automation enabled and the active WhatsAppSession cannot send',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
  // Phase 6.7: which branches the ready-for-collection rule governs, as the resolver would decide it now.
  metric({
    code: 'AUTOMATION_RULES',
    kpiId: 'M7',
    area: 'CRM',
    panel: 'CRM_AUTOMATION_SETTINGS',
    type: SNAPSHOT,
    unit: COUNT,
    dateSemantics: NOW,
    dateSource: 'communication_automation_settings: branch rule, else tenant default',
    perCurrency: false,
    dimensions: [ReportDimension.BRANCH],
    filters: [ReportFilterCode.BRANCH],
    status: A,
  }),
];

/** Concepts Phase 6 refuses to compute. No metric may ever carry one of these codes or prefixes. */
export const REPORT_FORBIDDEN_METRIC_CODES: readonly string[] = [
  'PROFIT',
  'GROSS_PROFIT',
  'NET_PROFIT',
  'MARGIN',
  'COGS',
  // Phase 6.4: no exact historical stock reconstruction exists, so none of these may be offered.
  'HISTORICAL_STOCK_ON_HAND',
  'HISTORICAL_INVENTORY_VALUE',
  'STOCK_ON_HAND_TREND',
  'WHATSAPP_DELIVERED',
  'WHATSAPP_READ_RATE',
  // Phase 6.7: PROVIDER_ACCEPTED is not delivery, and nothing proves reading, replies or conversions.
  'WHATSAPP_READ',
  'MESSAGES_DELIVERED',
  'MESSAGES_READ',
  'DELIVERY_RATE',
  'DELIVERY_SUCCESS_RATE',
  'READ_RATE',
  'RESPONSE_RATE',
  'CONVERSION_RATE',
  'CAMPAIGN_ROI',
  // Phase 6.5: collections, expenses and supplier payments are separate flows; none of these is any of them combined.
  'EBITDA',
  'CASH_FLOW',
  'NET_INCOME',
  'NET_FINANCE',
];
export const REPORT_FORBIDDEN_METRIC_PREFIXES: readonly string[] = ['CLINICAL_'];

export function reportMetric(code: string): ReportMetricDefinition | undefined {
  return REPORT_METRICS.find((definition) => definition.code === code);
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export interface ReportBranchMeta {
  id: string;
  code: string;
  name: string;
  timezone: string;
}

/** Everything a figure needs to be interpreted: which days, which calendars, which branches. */
export interface ReportMeta {
  preset: ReportPreset;
  period: ReportDateRange;
  comparison: ReportDateRange | null;
  granularity: ReportGranularity;
  /** The calendar the preset labels were taken from. */
  labelTimezone: string;
  tenantTimezone: string;
  branches: ReportBranchMeta[];
  weekStartsOn: ReportWeekStart;
  baseCurrencyCode: string;
  /** The tenant business date "as of now" figures are judged against. */
  asOfDate: string;
  generatedAt: string;
  panels: ReportPanel[];
}

export interface FlowMoneyFigure {
  current: MoneyByCurrency;
  previous: MoneyByCurrency | null;
  change: MoneyChange[] | null;
}

export interface FlowCountFigure {
  current: number;
  previous: number | null;
  change: CountChange | null;
}

export interface TrendBucket {
  from: string;
  to: string;
}

export interface MoneyTrendSeries {
  currencyCode: string;
  current: number[];
  /** Aligned by position with the previous period's buckets; null where that period has no such bucket. */
  previous: (number | null)[] | null;
}

export interface OverviewSalesPanel {
  netSales: FlowMoneyFigure;
  confirmedSales: MoneyByCurrency;
  returns: MoneyByCurrency;
  cancellations: MoneyByCurrency;
  confirmedOrders: FlowCountFigure;
  trend: {
    granularity: ReportGranularity;
    buckets: TrendBucket[];
    previousBuckets: TrendBucket[] | null;
    series: MoneyTrendSeries[];
  };
  byBranch: {
    branchId: string;
    branchCode: string;
    branchName: string;
    netSales: MoneyByCurrency;
  }[];
}

export interface OverviewAmountCount {
  amount: MoneyByCurrency;
  count: number;
}

export interface OverviewInventoryPanel {
  lowStockLines: number;
  outOfStockLines: number;
  slowMovingLines: number;
  deadStockLines: number;
  unknownMovementLines: number;
  slowMovingDays: number;
  deadStockDays: number;
}

export interface OverviewInventoryValuePanel {
  estimatedValue: MoneyByCurrency;
  costedLineCount: number;
  totalOnHandLineCount: number;
  lineCoverageBasisPoints: number | null;
  partialEstimate: boolean;
}

export interface OverviewOpticalPanel {
  pipeline: {
    ORDERED: number;
    SENT_TO_LAB: number;
    IN_LAB: number;
    RECEIVED_FROM_LAB: number;
    READY_FOR_COLLECTION: number;
  };
  readyPastThreshold: number;
  labPastThreshold: number;
  orderedNotSentPastThreshold: number;
  readyForCollectionAttentionDays: number;
  labStageAttentionDays: number;
  orderedNotSentAttentionDays: number;
}

export interface OverviewReport {
  meta: ReportMeta;
  panels: {
    sales?: OverviewSalesPanel;
    collections?: { collections: FlowMoneyFigure };
    receivables?: { outstanding: MoneyByCurrency; overdueInstalments: OverviewAmountCount };
    payables?: { outstanding: MoneyByCurrency; overdueInvoices: OverviewAmountCount };
    expenses?: { expenses: FlowMoneyFigure };
    inventory?: OverviewInventoryPanel;
    inventoryValue?: OverviewInventoryValuePanel;
    optical?: OverviewOpticalPanel;
    crm?: { failed: number; outcomeUnknown: number };
    whatsapp?: { automationEnabledBranches: number; automationUnavailableBranches: number };
  };
}

export interface OverviewReportQuery {
  preset?: ReportPreset;
  from?: string;
  to?: string;
  compare?: 'previous' | 'none';
  branchId?: string;
}

// ── Sales (Phase 6.3) ────────────────────────────────────────────────────────────────────────────

/** How a breakdown row is named when the thing it groups by no longer exists, or never did. */
export const REPORT_UNATTRIBUTED = 'UNATTRIBUTED' as const;

/**
 * One row of a sales breakdown.
 *
 * `id` is null for the unattributed row: the operator who rang the sale is no longer a user, or the
 * product's category was cleared. Those sales are still sales, so they keep their money and their
 * count and are shown rather than dropped — a breakdown whose rows do not add up to the headline is
 * worse than one with an honest "unknown" row.
 */
export interface SalesBreakdownRow {
  id: string | null;
  label: string | null;
  netSales: MoneyByCurrency;
  confirmedSales: MoneyByCurrency;
  returns: MoneyByCurrency;
  cancellations: MoneyByCurrency;
  confirmedOrders: number;
}

/** A product ranking row. Quantity is comparable across currencies; money is not, so it stays split. */
export interface SalesProductRow {
  rank: number;
  variantId: string | null;
  productId: string | null;
  sku: string | null;
  name: string | null;
  categoryId: string | null;
  categoryName: string | null;
  brandId: string | null;
  brandName: string | null;
  quantity: number;
  netSales: MoneyByCurrency;
}

export const SalesProductRanking = {
  QUANTITY: 'QUANTITY',
  NET_SALES: 'NET_SALES',
} as const;
export type SalesProductRanking = (typeof SalesProductRanking)[keyof typeof SalesProductRanking];

export const SalesBreakdownDimension = {
  BRANCH: 'BRANCH',
  OPERATOR: 'OPERATOR',
  PRODUCT: 'PRODUCT',
  VARIANT: 'VARIANT',
  CATEGORY: 'CATEGORY',
  BRAND: 'BRAND',
} as const;
export type SalesBreakdownDimension =
  (typeof SalesBreakdownDimension)[keyof typeof SalesBreakdownDimension];

/** The most rows any ranking or breakdown returns, as Phase 6.3 fixes it. */
export const SALES_BREAKDOWN_LIMIT = 50;

/**
 * Average Order Value, per currency.
 *
 * Deliberately its own shape rather than a `MoneyByCurrency`: a period with no confirmed orders has
 * no average, and that is `null` — never zero, which would read as "the average sale was nothing".
 */
export interface SalesAverageOrderValue {
  currencyCode: string;
  amountMinor: number | null;
  confirmedOrders: number;
}

export interface SalesHeadline {
  confirmedSales: FlowMoneyFigure;
  returns: FlowMoneyFigure;
  cancellations: FlowMoneyFigure;
  netSales: FlowMoneyFigure;
  confirmedOrders: FlowCountFigure;
  averageOrderValue: {
    current: SalesAverageOrderValue[];
    previous: SalesAverageOrderValue[] | null;
    change: MoneyChange[] | null;
  };
}

/**
 * Returns, told twice on purpose.
 *
 * `money` is the credit note's — it is what the customer stopped owing, dated when the note was
 * issued (or added back when it was voided). `recorded` and `quantity` are the Return domain's —
 * goods coming back, dated when the return was taken. A return taken on Monday whose note is issued
 * on Tuesday belongs to both days, each in its own column, and no report may quietly pick one date
 * for both.
 */
export interface SalesReturnsPanel {
  money: FlowMoneyFigure;
  recorded: FlowCountFigure;
  quantity: FlowCountFigure;
  /** Returns recorded per branch, from the Return domain: `confirmedOrders` holds the count and the money fields are empty, because a return's money is a credit-note event reported in `money`. */
  byBranch: SalesBreakdownRow[];
  topProducts: SalesProductRow[];
}

/**
 * Cancellations, split by whether the sale had ever been counted.
 *
 * Only `reversals` is money: those sales entered reporting truth and leave it again on the day they
 * were cancelled. `beforeConfirmation` is context — a count of orders that never became revenue, so
 * subtracting them would remove money that was never added.
 */
export interface SalesCancellationsPanel {
  reversals: FlowMoneyFigure;
  reversedOrders: FlowCountFigure;
  beforeConfirmationOrders: FlowCountFigure;
  byBranch: SalesBreakdownRow[];
}

export interface SalesTrend {
  granularity: ReportGranularity;
  buckets: TrendBucket[];
  previousBuckets: TrendBucket[] | null;
  series: MoneyTrendSeries[];
}

/** What the caller asked for, echoed back with the names of the entities it resolved. */
export interface SalesFiltersApplied {
  branchId: string | null;
  operatorId: string | null;
  operatorName: string | null;
  productId: string | null;
  productName: string | null;
  variantId: string | null;
  variantSku: string | null;
  categoryId: string | null;
  categoryName: string | null;
  brandId: string | null;
  brandName: string | null;
}

/**
 * Which parts of this report read live catalogue data rather than what was recorded at the sale.
 *
 * A sale freezes its product's SKU and name; it does not freeze the category or brand, which are
 * attributes of the product row as it stands today. Re-categorise a frame and last year's sales
 * move with it. The API says so here so no screen has to infer it, and so a caller integrating
 * against this cannot mistake it for history.
 */
export interface SalesAttributeDisclosure {
  identityFrozenAtSale: readonly ['SKU', 'NAME'];
  currentCatalogueAttributes: readonly ['CATEGORY', 'BRAND'];
}

export interface SalesReport {
  meta: ReportMeta;
  filters: SalesFiltersApplied;
  disclosure: SalesAttributeDisclosure;
  headline: SalesHeadline;
  trend: SalesTrend;
  byBranch: SalesBreakdownRow[];
  byOperator: SalesBreakdownRow[];
  byCategory: SalesBreakdownRow[];
  byBrand: SalesBreakdownRow[];
  topProducts: SalesProductRow[];
  lowestProducts: SalesProductRow[];
  returns: SalesReturnsPanel;
  cancellations: SalesCancellationsPanel;
}

export interface SalesReportQuery extends OverviewReportQuery {
  operatorId?: string;
  productId?: string;
  variantId?: string;
  categoryId?: string;
  brandId?: string;
  ranking?: SalesProductRanking;
}

/** The record kinds a Sales figure can be opened into. */
export const SalesDrilldownKind = {
  CONFIRMED_ORDERS: 'CONFIRMED_ORDERS',
  RETURNS: 'RETURNS',
  CANCELLATIONS: 'CANCELLATIONS',
} as const;
export type SalesDrilldownKind = (typeof SalesDrilldownKind)[keyof typeof SalesDrilldownKind];

/**
 * What happened to a drilldown record on its business date.
 *
 * A RETURNS drilldown is a stream of credit-note events, not a list of credit notes: the issue
 * adds the note's amount to Returns (and takes it off Net Sales) on the day it was issued, and a
 * later void takes it back off Returns (and puts it back on Net Sales) on the day of the void.
 * `amountMinor` is signed as a contribution to the figure the drilldown opens, so the rows of a
 * period always add up to that figure.
 */
export const SalesDrilldownEvent = {
  ORDER_CONFIRMED: 'ORDER_CONFIRMED',
  CONFIRMED_ORDER_CANCELLED: 'CONFIRMED_ORDER_CANCELLED',
  CREDIT_NOTE_ISSUED: 'CREDIT_NOTE_ISSUED',
  CREDIT_NOTE_VOIDED: 'CREDIT_NOTE_VOIDED',
} as const;
export type SalesDrilldownEvent = (typeof SalesDrilldownEvent)[keyof typeof SalesDrilldownEvent];

export interface SalesDrilldownRow {
  id: string;
  kind: SalesDrilldownKind;
  event: SalesDrilldownEvent;
  reference: string;
  occurredAt: string;
  businessDate: string;
  branchId: string;
  branchName: string;
  operatorId: string | null;
  operatorName: string | null;
  customerName: string | null;
  currencyCode: string;
  amountMinor: number;
}

export interface SalesDrilldownQuery extends SalesReportQuery {
  kind: SalesDrilldownKind;
  page?: number;
  pageSize?: number;
}

// ===========================================================================
// Inventory tab (Phase 6.4)
// ===========================================================================

/**
 * Where a current on-hand stock line sits in time.
 *
 * Only lines holding at least `max(1, minimumOnHandForSlowMoving)` are aged; a positive line below
 * that is BELOW_AGEING_MINIMUM, and a line at or below zero has no bucket at all (it is out of
 * stock, not dead). UNKNOWN_HISTORY is never slow or dead. The buckets never overlap.
 */
export const StockAgeingBucket = {
  RECENT: 'RECENT',
  SLOW: 'SLOW',
  DEAD: 'DEAD',
  UNKNOWN_HISTORY: 'UNKNOWN_HISTORY',
  BELOW_AGEING_MINIMUM: 'BELOW_AGEING_MINIMUM',
} as const;
export type StockAgeingBucket = (typeof StockAgeingBucket)[keyof typeof StockAgeingBucket];

/** The operational low/out rule (`stockAlertState` in `@optiflow/shared`), as a report value. */
export const StockAlertLevel = {
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  LOW_STOCK: 'LOW_STOCK',
  OK: 'OK',
} as const;
export type StockAlertLevel = (typeof StockAlertLevel)[keyof typeof StockAlertLevel];

/**
 * A ledger movement, named by its type and where it came from.
 *
 * Derived from `stock_transactions.type` and `reference_type` only — the categories the domain
 * actually writes. A reference nobody recognises lands in an OTHER_* category rather than vanishing.
 */
export const StockMovementCategory = {
  SALE_OUT: 'SALE_OUT',
  MANUAL_ISSUE: 'MANUAL_ISSUE',
  OTHER_ISSUE: 'OTHER_ISSUE',
  ISSUE_REVERSAL: 'ISSUE_REVERSAL',
  PURCHASE_RECEIPT: 'PURCHASE_RECEIPT',
  RETURN_RESTOCK: 'RETURN_RESTOCK',
  MANUAL_RECEIPT: 'MANUAL_RECEIPT',
  OTHER_RECEIPT: 'OTHER_RECEIPT',
  TRANSFER_OUT: 'TRANSFER_OUT',
  TRANSFER_IN: 'TRANSFER_IN',
  ADJUSTMENT_IN: 'ADJUSTMENT_IN',
  ADJUSTMENT_OUT: 'ADJUSTMENT_OUT',
  STOCKTAKE_IN: 'STOCKTAKE_IN',
  STOCKTAKE_OUT: 'STOCKTAKE_OUT',
} as const;
export type StockMovementCategory =
  (typeof StockMovementCategory)[keyof typeof StockMovementCategory];
export const STOCK_MOVEMENT_CATEGORIES = Object.values(StockMovementCategory);

/**
 * A quantity within one product type.
 *
 * Frames, contact-lens boxes and bottles of solution are not interchangeable units, so no report
 * adds them together: every quantity is reported per product type. `productType` is null only for
 * a variant whose product row is missing.
 */
export interface ProductTypeQuantity {
  productType: string | null;
  quantity: number;
}

/** Current stock, "as of now". Never compared with a previous period, never re-dated by the filter. */
export interface InventorySnapshot {
  /** When the snapshot was read: always now, whatever period is selected. */
  asOf: string;
  stockLines: number;
  onHandLines: number;
  onHandVariants: number;
  unitsOnHand: ProductTypeQuantity[];
  lowStockLines: number;
  outOfStockLines: number;
  ageing: Record<StockAgeingBucket, number>;
  slowMovingDays: number;
  deadStockDays: number;
  minimumOnHandForSlowMoving: number;
}

export interface InventoryTypeCoverage {
  productType: string | null;
  onHandLines: number;
  costedLines: number;
  onHandUnits: number;
  costedUnits: number;
  /** Costed units ÷ units, within this product type only. */
  unitCoverageBasisPoints: number | null;
}

/** Present only with catalog.view. Catalogue cost is current master data, not acquisition cost. */
export interface InventoryValueSection extends OverviewInventoryValuePanel {
  missingCostLines: number;
  byProductType: InventoryTypeCoverage[];
}

export interface InventoryInTransit {
  openTransfers: number;
  openLines: number;
  units: ProductTypeQuantity[];
}

export interface InventoryBreakdownRow {
  id: string | null;
  label: string | null;
  onHandLines: number;
  lowStockLines: number;
  outOfStockLines: number;
  slowMovingLines: number;
  deadStockLines: number;
  unknownMovementLines: number;
  units: ProductTypeQuantity[];
}

export interface InventoryMovementRow {
  category: StockMovementCategory;
  movements: number;
  /** Signed: positive into the branch, negative out of it. */
  units: ProductTypeQuantity[];
}

export interface InventoryMovementsSection {
  movements: number;
  byCategory: InventoryMovementRow[];
  trend: { granularity: ReportGranularity; buckets: TrendBucket[]; movements: number[] };
}

export interface InventoryTransfersSection {
  created: number;
  dispatched: number;
  completed: number;
  completedWithVariance: number;
  dispatchedUnits: ProductTypeQuantity[];
  receivedUnits: ProductTypeQuantity[];
  /** Final variance only: RECEIVED_WITH_VARIANCE lines, signed as the Transfers module signs it. */
  variance: { lines: number; units: ProductTypeQuantity[] };
}

export interface StocktakeVarianceByType {
  productType: string | null;
  increase: number;
  decrease: number;
  net: number;
}

export interface InventoryStocktakeSection {
  posted: number;
  countedLines: number;
  affectedLines: number;
  variance: StocktakeVarianceByType[];
}

export interface AdjustmentByType {
  productType: string | null;
  increase: number;
  decrease: number;
}

export interface InventoryAdjustmentsSection {
  movements: number;
  units: AdjustmentByType[];
  byReasonType: { reasonType: string | null; movements: number; units: AdjustmentByType[] }[];
}

export interface InventoryFiltersApplied {
  productId: string | null;
  productName: string | null;
  variantId: string | null;
  variantSku: string | null;
  categoryId: string | null;
  categoryName: string | null;
  brandId: string | null;
  brandName: string | null;
  movementCategory: StockMovementCategory | null;
}

export interface InventoryReport {
  meta: ReportMeta;
  filters: InventoryFiltersApplied;
  snapshot: InventorySnapshot;
  /** Omitted entirely without catalog.view. */
  value?: InventoryValueSection;
  inTransit: InventoryInTransit;
  byBranch: InventoryBreakdownRow[];
  byCategory: InventoryBreakdownRow[];
  byBrand: InventoryBreakdownRow[];
  /** Phase 6.8: the same classified stock grouped by product type; `id` and `label` are the product type. */
  byProductType: InventoryBreakdownRow[];
  movements: InventoryMovementsSection;
  transfers: InventoryTransfersSection;
  stocktakes: InventoryStocktakeSection;
  adjustments: InventoryAdjustmentsSection;
  /** Omitted entirely without sales.view. */
  zeroSales?: { lines: number; units: ProductTypeQuantity[] };
}

export interface InventoryReportQuery extends Omit<OverviewReportQuery, 'compare'> {
  productId?: string;
  variantId?: string;
  categoryId?: string;
  brandId?: string;
  movementCategory?: StockMovementCategory;
}

/** The record lists behind the Inventory figures. */
export const InventoryDrilldownKind = {
  STOCK_LINES: 'STOCK_LINES',
  MOVEMENTS: 'MOVEMENTS',
  IN_TRANSIT: 'IN_TRANSIT',
  TRANSFER_VARIANCE: 'TRANSFER_VARIANCE',
  STOCKTAKE_LINES: 'STOCKTAKE_LINES',
  ZERO_SALES: 'ZERO_SALES',
} as const;
export type InventoryDrilldownKind =
  (typeof InventoryDrilldownKind)[keyof typeof InventoryDrilldownKind];

/** Narrows STOCK_LINES: an ageing bucket, an alert level, or the cost split (catalog.view only). */
export const InventoryStockHealth = {
  ...StockAgeingBucket,
  LOW_STOCK: 'LOW_STOCK',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  COSTED: 'COSTED',
  MISSING_COST: 'MISSING_COST',
} as const;
export type InventoryStockHealth = (typeof InventoryStockHealth)[keyof typeof InventoryStockHealth];

export interface InventoryDrilldownQuery extends InventoryReportQuery {
  kind: InventoryDrilldownKind;
  health?: InventoryStockHealth;
  page?: number;
  pageSize?: number;
}

/**
 * One record behind an Inventory figure. Which fields are filled depends on the kind: a stock line
 * has quantity, alert and ageing; a movement has category and signed quantity; a transfer line has
 * dispatched, received and the domain's variance; a stocktake line has expected, counted and
 * variance. Cost appears only with catalog.view.
 */
export interface InventoryDrilldownRow {
  id: string;
  kind: InventoryDrilldownKind;
  reference: string | null;
  occurredAt: string | null;
  businessDate: string | null;
  branchId: string;
  branchName: string;
  counterpartBranchName: string | null;
  variantId: string;
  sku: string;
  productName: string | null;
  productType: string | null;
  quantity: number | null;
  alert: StockAlertLevel | null;
  ageing: StockAgeingBucket | null;
  ageDays: number | null;
  category: StockMovementCategory | null;
  dispatched: number | null;
  received: number | null;
  expected: number | null;
  counted: number | null;
  variance: number | null;
  costMinor: number | null;
  valueMinor: number | null;
}

// ---------------------------------------------------------------------------
// Finance (Phase 6.5)
// ---------------------------------------------------------------------------

/**
 * The Finance tab's sections. Each belongs to one panel, so a missing permission removes it; each
 * also appears only when every active filter applies to it, so a customer filter never leaves an
 * unfiltered expense total on screen beside filtered collections.
 */
export const FinanceSection = {
  COLLECTIONS: 'COLLECTIONS',
  RECEIVABLES: 'RECEIVABLES',
  SUPPLIER_INVOICES: 'SUPPLIER_INVOICES',
  PAYABLES: 'PAYABLES',
  SUPPLIER_PAYMENTS: 'SUPPLIER_PAYMENTS',
  EXPENSES: 'EXPENSES',
} as const;
export type FinanceSection = (typeof FinanceSection)[keyof typeof FinanceSection];

export const FINANCE_SECTION_PANELS: Readonly<Record<FinanceSection, ReportPanel>> = {
  COLLECTIONS: ReportPanel.FINANCE_COLLECTIONS,
  RECEIVABLES: ReportPanel.FINANCE_RECEIVABLES,
  SUPPLIER_INVOICES: ReportPanel.FINANCE_SUPPLIER_OBLIGATIONS,
  PAYABLES: ReportPanel.FINANCE_PAYABLES,
  SUPPLIER_PAYMENTS: ReportPanel.FINANCE_PAYABLES,
  EXPENSES: ReportPanel.FINANCE_EXPENSES,
};

/** Which sections each entity filter can truthfully narrow. Currency and branch narrow every section. */
export const FINANCE_FILTER_SECTIONS = {
  customerId: ['COLLECTIONS', 'RECEIVABLES'],
  supplierId: ['SUPPLIER_INVOICES', 'PAYABLES', 'SUPPLIER_PAYMENTS', 'EXPENSES'],
  expenseCategoryId: ['EXPENSES'],
  paymentMethod: ['COLLECTIONS', 'SUPPLIER_PAYMENTS', 'EXPENSES'],
} as const satisfies Record<string, readonly FinanceSection[]>;

/** An amount in each currency present, with how many records made it up. */
export interface FinanceMoneyCount {
  amount: MoneyByCurrency;
  count: number;
}

/** A breakdown row: a branch, supplier or category, possibly unattributed (id null). */
export interface FinanceBreakdownRow {
  id: string | null;
  label: string | null;
  /** The Arabic name, where the supplier or category has one. */
  labelAr: string | null;
  amount: MoneyByCurrency;
  count: number;
}

/** Collections by payment method, one row per method and currency: payments in, refunds out, net. */
export interface FinanceMethodRow {
  method: PaymentMethod;
  currencyCode: string;
  paymentsMinor: number;
  refundsMinor: number;
  netMinor: number;
  paymentCount: number;
  refundCount: number;
}

export interface FinanceSupplierMethodRow {
  method: string;
  currencyCode: string;
  amountMinor: number;
  count: number;
}

export interface FinanceAgeingRow {
  bucket: AgeingBucket;
  amount: MoneyByCurrency;
  count: number;
}

export interface FinanceTrend {
  granularity: ReportGranularity;
  buckets: TrendBucket[];
  series: { currencyCode: string; values: number[] }[];
}

export interface FinanceCollectionsSection {
  /** Payments minus refunds, dated when the money moved. */
  collections: MoneyByCurrency;
  payments: FinanceMoneyCount;
  refunds: FinanceMoneyCount;
  byMethod: FinanceMethodRow[];
  byBranch: FinanceBreakdownRow[];
  trend: FinanceTrend;
}

/**
 * Two separate current figures. Outstanding is every order that still owes (order branch); scheduled
 * is what active payment-plan instalments still owe (plan branch). Only the scheduled population is
 * aged, and the two are never reconciled to each other because the domain does not make them equal.
 */
export interface FinanceReceivablesSection {
  outstanding: MoneyByCurrency;
  owingOrders: number;
  byBranch: FinanceBreakdownRow[];
  scheduled: FinanceMoneyCount;
  scheduledByBranch: FinanceBreakdownRow[];
  ageing: FinanceAgeingRow[];
  overdue: FinanceMoneyCount;
}

export interface FinanceSupplierInvoicesSection {
  invoiced: FinanceMoneyCount;
  bySupplier: FinanceBreakdownRow[];
  byBranch: FinanceBreakdownRow[];
  trend: FinanceTrend;
}

export interface FinanceSettlementRow {
  settlement: PayableSettlement;
  count: number;
  outstanding: MoneyByCurrency;
}

export interface FinancePayablesSection {
  outstanding: MoneyByCurrency;
  openInvoices: number;
  overdue: FinanceMoneyCount;
  ageing: FinanceAgeingRow[];
  bySettlement: FinanceSettlementRow[];
  bySupplier: FinanceBreakdownRow[];
  byBranch: FinanceBreakdownRow[];
}

export interface FinanceSupplierPaymentsSection {
  /** Each POSTED payment once, however many invoices it was allocated to. */
  paid: FinanceMoneyCount;
  byMethod: FinanceSupplierMethodRow[];
  bySupplier: FinanceBreakdownRow[];
  byBranch: FinanceBreakdownRow[];
  trend: FinanceTrend;
}

export interface FinanceExpensesSection {
  expenses: FinanceMoneyCount;
  byCategory: FinanceBreakdownRow[];
  byBranch: FinanceBreakdownRow[];
  byMethod: FinanceSupplierMethodRow[];
  trend: FinanceTrend;
}

export interface FinanceFiltersApplied {
  currencyCode: string | null;
  paymentMethod: PaymentMethod | null;
  customerId: string | null;
  customerName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  expenseCategoryId: string | null;
  expenseCategoryName: string | null;
}

/** Breakdown lists are capped; the rest of a list is reachable through its drilldown. */
export const FINANCE_BREAKDOWN_LIMIT = 50;

export interface FinanceReport {
  meta: ReportMeta;
  filters: FinanceFiltersApplied;
  /** False without customers.view: no customer name reaches this account, in any section or record. */
  customerIdentity: boolean;
  /** Authorised sections an active filter cannot narrow, so they are left out rather than shown unfiltered. */
  notApplicable: FinanceSection[];
  collections?: FinanceCollectionsSection;
  receivables?: FinanceReceivablesSection;
  supplierInvoices?: FinanceSupplierInvoicesSection;
  payables?: FinancePayablesSection;
  supplierPayments?: FinanceSupplierPaymentsSection;
  expenses?: FinanceExpensesSection;
}

export interface FinanceReportQuery extends Omit<OverviewReportQuery, 'compare'> {
  currencyCode?: string;
  paymentMethod?: PaymentMethod;
  customerId?: string;
  supplierId?: string;
  expenseCategoryId?: string;
}

export const FinanceDrilldownKind = {
  COLLECTION_TRANSACTIONS: 'COLLECTION_TRANSACTIONS',
  RECEIVABLE_ORDERS: 'RECEIVABLE_ORDERS',
  SCHEDULED_INSTALMENTS: 'SCHEDULED_INSTALMENTS',
  PAYABLE_INVOICES: 'PAYABLE_INVOICES',
  SUPPLIER_INVOICES: 'SUPPLIER_INVOICES',
  SUPPLIER_PAYMENTS: 'SUPPLIER_PAYMENTS',
  EXPENSES: 'EXPENSES',
} as const;
export type FinanceDrilldownKind = (typeof FinanceDrilldownKind)[keyof typeof FinanceDrilldownKind];

export const FINANCE_DRILLDOWN_SECTIONS: Readonly<Record<FinanceDrilldownKind, FinanceSection>> = {
  COLLECTION_TRANSACTIONS: 'COLLECTIONS',
  RECEIVABLE_ORDERS: 'RECEIVABLES',
  SCHEDULED_INSTALMENTS: 'RECEIVABLES',
  PAYABLE_INVOICES: 'PAYABLES',
  SUPPLIER_INVOICES: 'SUPPLIER_INVOICES',
  SUPPLIER_PAYMENTS: 'SUPPLIER_PAYMENTS',
  EXPENSES: 'EXPENSES',
};

export const FinanceMovement = { PAYMENT: 'PAYMENT', REFUND: 'REFUND' } as const;
export type FinanceMovement = (typeof FinanceMovement)[keyof typeof FinanceMovement];

export interface FinanceDrilldownPage {
  rows: FinanceDrilldownRow[];
  total: number;
  /** What every matching record adds up to, per currency: the figure this list opens from. */
  totals: MoneyByCurrency;
  page: number;
  pageSize: number;
}

export interface FinanceDrilldownQuery extends FinanceReportQuery {
  kind: FinanceDrilldownKind;
  /** Collection transactions only. */
  movement?: FinanceMovement;
  /** Scheduled instalments and payable invoices only. */
  bucket?: AgeingBucket;
  /** Payable invoices only. */
  settlement?: PayableSettlement;
  page?: number;
  pageSize?: number;
}

/**
 * One record behind a Finance figure. `amountMinor` is exactly what the record contributes to the
 * figure it opens from — signed for collections, outstanding for a snapshot — so a page adds up.
 * `party` is a customer only with customers.view, otherwise always null; a supplier or payee
 * appears only in supplier and expense records.
 */
export interface FinanceDrilldownRow {
  id: string;
  kind: FinanceDrilldownKind;
  reference: string | null;
  orderReference: string | null;
  businessDate: string | null;
  dueDate: string | null;
  branchId: string;
  branchName: string;
  party: string | null;
  partyAr: string | null;
  categoryName: string | null;
  categoryNameAr: string | null;
  method: string | null;
  movement: FinanceMovement | null;
  sequence: number | null;
  currencyCode: string;
  amountMinor: number;
  totalMinor: number | null;
  settledMinor: number | null;
  daysOverdue: number | null;
  bucket: AgeingBucket | null;
  settlement: PayableSettlement | null;
}

// ---------------------------------------------------------------------------
// Customers (Phase 6.6)
// ---------------------------------------------------------------------------

/** How the customer lists are ordered: by net sales in the base currency, or by confirmed orders. */
export const CustomerRanking = { NET_SALES: 'NET_SALES', ORDERS: 'ORDERS' } as const;
export type CustomerRanking = (typeof CustomerRanking)[keyof typeof CustomerRanking];

/** The most customers a ranking returns; the rest are reachable through the drilldown. */
export const CUSTOMER_RANKING_LIMIT = 50;

/** A count per trend bucket: registrations are counts, never money. */
export interface CountTrend {
  granularity: ReportGranularity;
  buckets: TrendBucket[];
  values: number[];
}

/**
 * New customers, by the branch that registered them. `Customer.createdBranchId` means exactly this
 * and nothing else: it is not who owns the customer, where they buy, or where they owe.
 */
export interface CustomerRegistrationsSection {
  /** Every customer created in the period, whether or not a branch registered them. */
  registered: number;
  byBranch: { branchId: string; branchName: string; registered: number }[];
  /**
   * Customers created with no registration branch. Counted in `registered` and never assigned to a
   * branch; null when one registration branch is selected, because they then fall outside it.
   */
  noRegistrationBranch: number | null;
  trend: CountTrend;
}

/** One customer's canonical Sales figures for the period, attributed through the order. */
export interface CustomerSalesRow {
  customerId: string;
  customerNumber: string | null;
  name: string | null;
  confirmedOrders: number;
  confirmedSales: MoneyByCurrency;
  returns: MoneyByCurrency;
  cancellations: MoneyByCurrency;
  netSales: MoneyByCurrency;
}

/**
 * Customer sales activity, from the canonical Sales rows split by the order's customer.
 *
 * Customer net sales plus walk-in net sales is the Sales tab's Net Sales for the same scope and
 * period, per currency. Every branch figure is the order's branch, never the registering branch.
 */
export interface CustomerSalesSection {
  buyingCustomers: number;
  returningCustomers: number;
  firstTimeBuyers: number;
  customerNetSales: MoneyByCurrency;
  customerConfirmedOrders: number;
  walkIn: { netSales: MoneyByCurrency; confirmedOrders: number };
  /** Canonical Net Sales and Confirmed Orders for the whole scope: customers plus walk-ins. */
  netSales: MoneyByCurrency;
  confirmedOrders: number;
  byBranch: {
    branchId: string;
    branchName: string;
    buyingCustomers: number;
    customerNetSales: MoneyByCurrency;
    customerConfirmedOrders: number;
  }[];
  topCustomers: CustomerSalesRow[];
}

export interface CustomerReceivableRow {
  customerId: string;
  customerNumber: string | null;
  name: string | null;
  outstanding: MoneyByCurrency;
  owingOrders: number;
}

/**
 * Customer receivables as of now, from the canonical order receivable, by the order's branch.
 * Customer outstanding plus walk-in outstanding is the Finance tab's receivables outstanding.
 */
export interface CustomerReceivablesSection {
  outstanding: MoneyByCurrency;
  owingCustomers: number;
  owingOrders: number;
  walkIn: { outstanding: MoneyByCurrency; owingOrders: number };
  /** Canonical Customer receivables outstanding (C4) for the whole scope. */
  total: MoneyByCurrency;
  byBranch: {
    branchId: string;
    branchName: string;
    outstanding: MoneyByCurrency;
    owingCustomers: number;
  }[];
  topCustomers: CustomerReceivableRow[];
}

export interface CustomersReport {
  meta: ReportMeta;
  ranking: CustomerRanking;
  registrations: CustomerRegistrationsSection;
  /** Omitted entirely without sales.view. */
  sales?: CustomerSalesSection;
  /** Omitted entirely without sales.view and payments.view. */
  receivables?: CustomerReceivablesSection;
}

export interface CustomersReportQuery extends Omit<OverviewReportQuery, 'compare'> {
  ranking?: CustomerRanking;
}

export const CustomersDrilldownKind = {
  REGISTRATIONS: 'REGISTRATIONS',
  CUSTOMER_ACTIVITY: 'CUSTOMER_ACTIVITY',
  OWING_CUSTOMERS: 'OWING_CUSTOMERS',
  CUSTOMER_SALES: 'CUSTOMER_SALES',
  CUSTOMER_OWING_ORDERS: 'CUSTOMER_OWING_ORDERS',
} as const;
export type CustomersDrilldownKind =
  (typeof CustomersDrilldownKind)[keyof typeof CustomersDrilldownKind];

/** The panel each Customers record list belongs to. */
export const CUSTOMERS_DRILLDOWN_PANELS: Readonly<Record<CustomersDrilldownKind, ReportPanel>> = {
  REGISTRATIONS: ReportPanel.CUSTOMERS_REGISTRATIONS,
  CUSTOMER_ACTIVITY: ReportPanel.CUSTOMERS_VALUE,
  CUSTOMER_SALES: ReportPanel.CUSTOMERS_VALUE,
  OWING_CUSTOMERS: ReportPanel.CUSTOMERS_OUTSTANDING,
  CUSTOMER_OWING_ORDERS: ReportPanel.CUSTOMERS_OUTSTANDING,
};

export interface CustomersDrilldownQuery extends CustomersReportQuery {
  kind: CustomersDrilldownKind;
  /** CUSTOMER_SALES and CUSTOMER_OWING_ORDERS only: the customer whose records these are. */
  customerId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * One record behind a Customers figure. A customer row (REGISTRATIONS, CUSTOMER_ACTIVITY,
 * OWING_CUSTOMERS) carries the customer and, where it has money, `amount` per currency; an order
 * row (CUSTOMER_SALES, CUSTOMER_OWING_ORDERS) carries one currency and a signed `amountMinor`.
 */
export interface CustomersDrilldownRow {
  id: string;
  kind: CustomersDrilldownKind;
  customerId: string | null;
  customerNumber: string | null;
  name: string | null;
  reference: string | null;
  event: SalesDrilldownEvent | null;
  businessDate: string | null;
  branchId: string | null;
  branchName: string | null;
  confirmedOrders: number | null;
  owingOrders: number | null;
  amount: MoneyByCurrency | null;
  currencyCode: string | null;
  amountMinor: number | null;
  totalMinor: number | null;
  settledMinor: number | null;
}

export interface CustomersDrilldownPage {
  rows: CustomersDrilldownRow[];
  total: number;
  /** What every matching record adds up to, per currency; empty for registrations, which are counts. */
  totals: MoneyByCurrency;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// Optical (Phase 6.6)
// ---------------------------------------------------------------------------

/** The open stages of an optical job, in lifecycle order. */
export const OPTICAL_PIPELINE_STAGES = [
  'ORDERED',
  'SENT_TO_LAB',
  'IN_LAB',
  'RECEIVED_FROM_LAB',
  'READY_FOR_COLLECTION',
] as const;
export type OpticalPipelineStage = (typeof OPTICAL_PIPELINE_STAGES)[number];

/** Which attention threshold a job has passed, judged exactly as the Overview judges it. */
export const OpticalAttention = {
  READY_PAST: 'READY_PAST',
  LAB_PAST: 'LAB_PAST',
  ORDERED_PAST: 'ORDERED_PAST',
} as const;
export type OpticalAttention = (typeof OpticalAttention)[keyof typeof OpticalAttention];

/**
 * What a delivered job's history proves, one class per job. A job is timed, from its CREATED→ORDERED
 * row to its DELIVERED row, exactly when both endpoints are reliable: COMPLETE (every forward stage
 * recorded in order) and INCOMPLETE_HISTORY (reliable endpoints, a gap between them). START_NOT_RECORDED
 * is a legacy job backfilled mid-life, with no ORDERED row; INVALID_HISTORY has endpoints that cannot be
 * relied on — DELIVERED before ORDERED, a repeated start or delivery, or a checkout job with no ORDERED
 * row. Neither is timed, and nothing is ever timed from created_at or updated_at.
 */
export const OpticalLifecycle = {
  COMPLETE: 'COMPLETE',
  INCOMPLETE_HISTORY: 'INCOMPLETE_HISTORY',
  START_NOT_RECORDED: 'START_NOT_RECORDED',
  INVALID_HISTORY: 'INVALID_HISTORY',
} as const;
export type OpticalLifecycle = (typeof OpticalLifecycle)[keyof typeof OpticalLifecycle];

/**
 * The delivered jobs behind one turnaround figure. TIMED is COMPLETE and INCOMPLETE_HISTORY together;
 * INCOMPLETE_HISTORY is every history that does not prove the whole path, timed or not (INCOMPLETE_HISTORY
 * and INVALID_HISTORY jobs), so the groups overlap exactly as the figures do.
 */
export const OpticalTurnaroundGroup = {
  TIMED: 'TIMED',
  START_NOT_RECORDED: 'START_NOT_RECORDED',
  INCOMPLETE_HISTORY: 'INCOMPLETE_HISTORY',
  INVALID_HISTORY: 'INVALID_HISTORY',
} as const;
export type OpticalTurnaroundGroup =
  (typeof OpticalTurnaroundGroup)[keyof typeof OpticalTurnaroundGroup];

/**
 * Where the latest ready-for-collection message of a job stands. Nothing here claims the customer
 * received or read anything: SENT_TO_WHATSAPP is the provider accepting it, and NOT_SENT is a decision.
 */
export const OpticalMessageState = {
  NONE: 'NONE',
  PENDING: 'PENDING',
  SENT_TO_WHATSAPP: 'SENT_TO_WHATSAPP',
  HANDED_OFF: 'HANDED_OFF',
  CONFIRMED_SENT: 'CONFIRMED_SENT',
  FAILED: 'FAILED',
  OUTCOME_UNKNOWN: 'OUTCOME_UNKNOWN',
  NOT_SENT: 'NOT_SENT',
} as const;
export type OpticalMessageState = (typeof OpticalMessageState)[keyof typeof OpticalMessageState];
export const OPTICAL_MESSAGE_STATES = Object.values(OpticalMessageState);

export interface OpticalTurnaroundFigures {
  /** Jobs timed: a reliable ORDERED start and one DELIVERED end, gaps between them allowed. */
  jobs: number;
  totalSeconds: number;
  averageSeconds: number | null;
  medianSeconds: number | null;
  minSeconds: number | null;
  maxSeconds: number | null;
}

export interface OpticalBranchRow {
  branchId: string;
  branchName: string;
  pipeline: Record<OpticalPipelineStage, number>;
  readyPastThreshold: number;
  labPastThreshold: number;
  orderedNotSentPastThreshold: number;
}

/**
 * Lab analysis, only where the lab is authoritative: a job's lab is fixed once it is sent, so jobs
 * still ORDERED are never attributed to a lab. `labId` null means no lab was recorded (legacy jobs).
 */
export interface OpticalLabRow {
  labId: string | null;
  labName: string | null;
  inHouse: boolean | null;
  atLab: number;
  back: number;
  labPastThreshold: number;
  turnaround: OpticalTurnaroundFigures;
}

export interface OpticalTurnaroundSection extends OpticalTurnaroundFigures {
  /** Jobs whose DELIVERED history row is in the period. The counts below overlap and need not add up to it. */
  delivered: number;
  /** Legacy jobs with no reliable ORDERED row: never timed. */
  startNotRecorded: number;
  /** Jobs whose history does not prove the whole forward path — timed ones with a gap, and every invalid one. */
  incompleteHistory: number;
  /** Jobs whose endpoints cannot be relied on: never timed, and also counted in incompleteHistory. */
  invalidHistory: number;
  byBranch: ({ branchId: string; branchName: string } & OpticalTurnaroundFigures)[];
}

export interface OpticalSupplySection {
  /** Jobs whose CREATED history row is in the period. */
  jobs: number;
  lenses: { mode: 'STOCKED' | 'MADE_TO_ORDER'; lenses: number; jobs: number }[];
  /** Created jobs with no lens recorded — legacy-style jobs. */
  jobsWithoutLens: number;
}

export interface OpticalReport {
  meta: ReportMeta;
  filters: { labId: string | null; labName: string | null };
  /** False without customers.view: no customer name reaches this account in any record. */
  customerIdentity: boolean;
  /** Current jobs, attention and thresholds: exactly the Overview's Optical panel for the same scope. */
  pipeline: OverviewOpticalPanel;
  activeJobs: number;
  byBranch: OpticalBranchRow[];
  byLab: OpticalLabRow[];
  flows: { created: number; delivered: number; cancelled: number };
  turnaround: OpticalTurnaroundSection;
  supply: OpticalSupplySection;
  /** Omitted entirely without notifications.view and customers.view. Adds up to the jobs ready for collection. */
  readyMessages?: { state: OpticalMessageState; jobs: number }[];
}

export interface OpticalReportQuery extends Omit<OverviewReportQuery, 'compare'> {
  labId?: string;
}

export const OpticalDrilldownKind = {
  ACTIVE_JOBS: 'ACTIVE_JOBS',
  ATTENTION_JOBS: 'ATTENTION_JOBS',
  DELIVERED_JOBS: 'DELIVERED_JOBS',
} as const;
export type OpticalDrilldownKind = (typeof OpticalDrilldownKind)[keyof typeof OpticalDrilldownKind];

export interface OpticalDrilldownQuery extends OpticalReportQuery {
  kind: OpticalDrilldownKind;
  /** ACTIVE_JOBS only. */
  status?: OpticalPipelineStage;
  /** ATTENTION_JOBS only, and required there. */
  attention?: OpticalAttention;
  /** DELIVERED_JOBS only: the jobs behind one turnaround figure. */
  lifecycle?: OpticalTurnaroundGroup;
  /** ACTIVE_JOBS of READY_FOR_COLLECTION only, and only with the message-state panel. */
  messageState?: OpticalMessageState;
  page?: number;
  pageSize?: number;
}

/** One job behind an Optical figure. Statuses, dates and references only — never a prescription. */
export interface OpticalDrilldownRow {
  id: string;
  kind: OpticalDrilldownKind;
  reference: string;
  orderReference: string;
  origin: 'CHECKOUT' | 'LEGACY';
  status: string;
  branchId: string;
  branchName: string;
  labName: string | null;
  /** Only with customers.view. */
  customerName: string | null;
  /** When the job entered its current status, from its history. */
  enteredAt: string | null;
  ageDays: number | null;
  orderedAt: string | null;
  deliveredAt: string | null;
  businessDate: string | null;
  turnaroundSeconds: number | null;
  lifecycle: OpticalLifecycle | null;
  /** Only with the message-state panel. */
  messageState: OpticalMessageState | null;
}

export interface OpticalDrilldownPage {
  rows: OpticalDrilldownRow[];
  total: number;
  /** DELIVERED_JOBS: the seconds of every timed job, so total ÷ timed jobs is the average shown. */
  totalSeconds: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// CRM (Phase 6.7)
// ---------------------------------------------------------------------------

/**
 * The CRM tab's sections. Communication history needs CRM_COMMUNICATIONS, current WhatsApp runtime
 * needs CRM_WHATSAPP_HEALTH and the automation rules need CRM_AUTOMATION_SETTINGS; a section whose
 * panel is missing is never queried. The two snapshots cannot be narrowed by a communication filter,
 * so with one active they are left out rather than shown unfiltered.
 */
export const CrmSection = {
  VOLUME: 'VOLUME',
  OUTCOMES: 'OUTCOMES',
  FAILURES: 'FAILURES',
  SUPPRESSIONS: 'SUPPRESSIONS',
  WHATSAPP: 'WHATSAPP',
  AUTOMATION: 'AUTOMATION',
} as const;
export type CrmSection = (typeof CrmSection)[keyof typeof CrmSection];

export const CRM_SECTION_PANELS: Readonly<Record<CrmSection, ReportPanel>> = {
  VOLUME: ReportPanel.CRM_COMMUNICATIONS,
  OUTCOMES: ReportPanel.CRM_COMMUNICATIONS,
  FAILURES: ReportPanel.CRM_COMMUNICATIONS,
  SUPPRESSIONS: ReportPanel.CRM_COMMUNICATIONS,
  WHATSAPP: ReportPanel.CRM_WHATSAPP_HEALTH,
  AUTOMATION: ReportPanel.CRM_AUTOMATION_SETTINGS,
};

/** The sections that read communication rows, and so the only ones a communication filter can narrow. */
export const CRM_COMMUNICATION_SECTIONS: readonly CrmSection[] = [
  'VOLUME',
  'OUTCOMES',
  'FAILURES',
  'SUPPRESSIONS',
];

/** A FAILED communication with no mapped failure code, reported as its own reason rather than dropped. */
export const CRM_UNRECORDED_FAILURE = 'UNRECORDED' as const;

/** Breakdown lists are capped; the rest of a list is reachable through its drilldown. */
export const CRM_BREAKDOWN_LIMIT = 50;

/** Counts split by delivery mode. Automated and manual are exhaustive, so they always add up to total. */
export interface CrmModeCounts {
  total: number;
  automated: number;
  manual: number;
}

export interface CrmStatusRow extends CrmModeCounts {
  status: CustomerCommunicationStatus;
}

export interface CrmPurposeRow extends CrmModeCounts {
  purpose: CommunicationPurpose;
}

/** A branch or template row; `id` null is custom text typed without a template. */
export interface CrmBreakdownRow extends CrmModeCounts {
  id: string | null;
  label: string | null;
  labelAr: string | null;
}

export interface CrmTrend {
  granularity: ReportGranularity;
  buckets: TrendBucket[];
  automated: number[];
  manual: number[];
}

/** Communications prepared in the period, dated by `prepared_at` on each branch's calendar. */
export interface CrmVolumeSection {
  prepared: FlowCountFigure;
  byMode: CrmModeCounts;
  byPurpose: CrmPurposeRow[];
  byBranch: CrmBreakdownRow[];
  byTemplate: CrmBreakdownRow[];
  trend: CrmTrend;
}

/**
 * Every communication whose current status was reached in the period, dated by that status's own
 * timestamp. All nine statuses are always listed and never merged: FAILED, CANCELLED and SUPPRESSED
 * stay apart, and PROVIDER_ACCEPTED means WhatsApp took the message — not that anybody received it.
 */
export interface CrmOutcomesSection {
  total: CrmModeCounts;
  byStatus: CrmStatusRow[];
}

export interface CrmFailureReasonRow {
  /** A mapped failure code, or `UNRECORDED`. Never a suppression reason. */
  code: string;
  count: number;
}

export interface CrmBranchCount {
  id: string;
  label: string;
  count: number;
}

/** The FAILED population of the outcomes, by failure code. "Outcome unknown" is counted apart. */
export interface CrmFailuresSection {
  total: number;
  failed: number;
  outcomeUnknown: number;
  byReason: CrmFailureReasonRow[];
  byBranch: CrmBranchCount[];
}

export interface CrmSuppressionReasonRow {
  reason: CommunicationSuppressionReason;
  count: number;
}

/** The SUPPRESSED population of the outcomes, by the reason it was withheld. */
export interface CrmSuppressionsSection {
  total: number;
  byReason: CrmSuppressionReasonRow[];
  byBranch: CrmBranchCount[];
}

/**
 * One branch's WhatsApp position right now. Configuration comes from the branch configuration row;
 * runtime comes only from the session of the active identity. No number, credential or error text.
 */
export interface CrmWhatsAppBranchRow {
  branchId: string;
  branchName: string;
  configured: boolean;
  enabled: boolean;
  automationEnabled: boolean;
  activeIdentity: WhatsAppIdentitySlot | null;
  /** Null when the active identity has no session at all. */
  runtimeStatus: WhatsAppSessionHealth | null;
  requiresReauth: boolean;
  adminDisabled: boolean;
  /** The active session is signed in as the number the branch says it sends from. */
  identityMatches: boolean;
  canSend: boolean;
  /** Automation switched on and the active identity cannot send now. Configured is not operational. */
  automationUnavailable: boolean;
  lastConnectedAt: string | null;
}

/** A snapshot as of now. There is no comparison because no history of runtime state is kept. */
export interface CrmWhatsAppSection {
  asOf: string;
  branches: number;
  configuredBranches: number;
  enabledBranches: number;
  automationEnabledBranches: number;
  automationUnavailableBranches: number;
  canSendBranches: number;
  rows: CrmWhatsAppBranchRow[];
}

export const CrmAutomationRuleSource = {
  BRANCH: 'BRANCH',
  TENANT: 'TENANT',
  NONE: 'NONE',
} as const;
export type CrmAutomationRuleSource =
  (typeof CrmAutomationRuleSource)[keyof typeof CrmAutomationRuleSource];

/** The ready-for-collection rule that governs one branch now: its own, the tenant default, or none. */
export interface CrmAutomationBranchRow {
  branchId: string;
  branchName: string;
  source: CrmAutomationRuleSource;
  /** Switched on with a template chosen — exactly what the resolver calls configured. */
  enabled: boolean;
  templateName: string | null;
  templateNameAr: string | null;
  /** Null without a template; false when its wording has been withdrawn. */
  templateActive: boolean | null;
}

export interface CrmAutomationSection {
  asOf: string;
  enabledBranches: number;
  disabledBranches: number;
  withoutRuleBranches: number;
  templateWithdrawnBranches: number;
  rows: CrmAutomationBranchRow[];
}

export interface CrmFiltersApplied {
  purpose: CommunicationPurpose | null;
  status: CustomerCommunicationStatus | null;
  deliveryMode: CommunicationDeliveryMode | null;
  templateId: string | null;
  templateName: string | null;
  suppressionReason: CommunicationSuppressionReason | null;
  failureCode: string | null;
}

export interface CrmReport {
  meta: ReportMeta;
  filters: CrmFiltersApplied;
  /** Authorised sections an active filter cannot narrow, so they are left out rather than shown unfiltered. */
  notApplicable: CrmSection[];
  volume?: CrmVolumeSection;
  outcomes?: CrmOutcomesSection;
  failures?: CrmFailuresSection;
  suppressions?: CrmSuppressionsSection;
  whatsapp?: CrmWhatsAppSection;
  automation?: CrmAutomationSection;
}

export interface CrmReportQuery extends OverviewReportQuery {
  purpose?: CommunicationPurpose;
  status?: CustomerCommunicationStatus;
  deliveryMode?: CommunicationDeliveryMode;
  templateId?: string;
  suppressionReason?: CommunicationSuppressionReason;
  failureCode?: string;
}

export const CrmDrilldownKind = {
  PREPARED: 'PREPARED',
  OUTCOMES: 'OUTCOMES',
  FAILED: 'FAILED',
  SUPPRESSED: 'SUPPRESSED',
  WHATSAPP_BRANCHES: 'WHATSAPP_BRANCHES',
  AUTOMATION_BRANCHES: 'AUTOMATION_BRANCHES',
} as const;
export type CrmDrilldownKind = (typeof CrmDrilldownKind)[keyof typeof CrmDrilldownKind];

export const CRM_DRILLDOWN_SECTIONS: Readonly<Record<CrmDrilldownKind, CrmSection>> = {
  PREPARED: 'VOLUME',
  OUTCOMES: 'OUTCOMES',
  FAILED: 'FAILURES',
  SUPPRESSED: 'SUPPRESSIONS',
  WHATSAPP_BRANCHES: 'WHATSAPP',
  AUTOMATION_BRANCHES: 'AUTOMATION',
};

/** Which branches a WhatsApp drilldown lists. */
export const CrmBranchView = {
  ALL: 'ALL',
  AUTOMATION_ENABLED: 'AUTOMATION_ENABLED',
  UNAVAILABLE: 'UNAVAILABLE',
} as const;
export type CrmBranchView = (typeof CrmBranchView)[keyof typeof CrmBranchView];

export interface CrmDrilldownQuery extends CrmReportQuery {
  kind: CrmDrilldownKind;
  /** OUTCOMES only: one status bucket. */
  bucketStatus?: CustomerCommunicationStatus;
  /** PREPARED and OUTCOMES: automated or manual. */
  bucketMode?: CommunicationDeliveryMode;
  /** FAILED: a failure code (or UNRECORDED); SUPPRESSED: a suppression reason. */
  bucketReason?: string;
  /** WHATSAPP_BRANCHES only. */
  branchView?: CrmBranchView;
  page?: number;
  pageSize?: number;
}

/**
 * One communication behind a CRM figure: the minimum an operator needs to find it. No body, no
 * recipient or sender number, no provider id. `customerName` is null without customers.view.
 */
export interface CrmCommunicationRecord {
  id: string;
  preparedAt: string;
  /** The timestamp that dates this record in the figure it opens from. */
  statusAt: string;
  businessDate: string;
  branchId: string;
  branchName: string;
  customerName: string | null;
  purpose: CommunicationPurpose;
  deliveryMode: CommunicationDeliveryMode;
  status: CustomerCommunicationStatus;
  templateName: string | null;
  templateNameAr: string | null;
  suppressionReason: CommunicationSuppressionReason | null;
  failureCode: string | null;
  attempts: number;
}

export interface CrmDrilldownPage {
  kind: CrmDrilldownKind;
  total: number;
  page: number;
  pageSize: number;
  communications: CrmCommunicationRecord[];
  whatsappBranches: CrmWhatsAppBranchRow[];
  automationBranches: CrmAutomationBranchRow[];
}
